import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { Icon } from '@famly/mf_data-display_icon';
import { Button } from '@famly/mf_input_button';
import { Text } from '@famly/mf_data-display_text';
import { createStack, Stack } from '@famly/mf_layout_stack';
import { createBox, Box } from '@famly/mf_layout_box';
import useTypedSelector from 'signin-app/components/hooks/use-typed-selector';
import translate from 'signin-app/helpers/translate';
import { StyledStack } from 'signin-app/child/check-in/going-home-with';
import { PersonImage } from 'signin-app/components/person-image';
import { getInitials } from 'signin-app/util/get-initials';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { useCustomWhitelabelColor } from 'signin-app/components/hooks/use-custom-whitelabel-color';
import * as Selectors from './selectors';
import * as Actions from './actions';
const ENTER_KEY_CODE = 13;
const StyledOverlay = styled(createStack({ flexDirection: 'column' })) `
    align-items: center;
    justify-content: center;
    position: fixed;
    background-color: grey;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999;
    // From designs:
    background-color: rgba(253, 253, 253, 0.92);
    backdrop-filter: blur(3px);
`;
export const StyledTextContainer = styled(createStack({
    alignItems: 'center',
    maxWidth: { base: '80px', tabletPortrait: '96px' },
    minHeight: { base: '28px', tabletPortrait: '32px' },
    mt: { base: 0.5, tabletPortrait: 1.5 },
    justifyContent: 'center',
})).withConfig({
    shouldForwardProp: prop => prop !== 'shouldWrap',
}) `
    justify-content: center;
    ${props => props.shouldWrap
    ? css `
                  text-align: center;
              `
    : css `
                  * {
                      text-overflow: ellipsis;
                      white-space: nowrap;
                      overflow: hidden;
                  }
              `}
`;
const SelectedStack = styled(createBox({})).withConfig({
    shouldForwardProp: prop => prop !== 'selected' && prop !== 'customBorderColor' && prop !== 'customBackgroundColor',
}) `
    border-radius: 16px;
    border: 2px solid ${props => (props.selected ? props.customBorderColor : 'transparent')};
    background-color: ${props => (props.selected ? props.customBackgroundColor : 'transparent')};
`;
const useAddPickupPerson = () => {
    const adding = useTypedSelector(Selectors.addingNewRelation);
    const pickupName = useTypedSelector(Selectors.getPickupPersonName);
    const dispatch = useDispatch();
    const handleShowPickupPersonField = React.useCallback(() => {
        dispatch(Actions.addNewPickupRelation.action(true));
    }, [dispatch]);
    const handleHidePickupPersonField = React.useCallback((newPickupName) => {
        if (hasValue(newPickupName)) {
            dispatch(Actions.addNewPickupRelation.action(false, newPickupName));
        }
        else {
            dispatch(Actions.addNewPickupRelation.action(false));
        }
    }, [dispatch]);
    return {
        adding,
        pickupName,
        handleShowPickupPersonField,
        handleHidePickupPersonField,
    };
};
export const AddPickupPerson = ({ childName, isExistingRelationSelected, size, }) => {
    const { adding, pickupName, handleShowPickupPersonField, handleHidePickupPersonField } = useAddPickupPerson();
    const p300 = useCustomWhitelabelColor('p300');
    const p200 = useCustomWhitelabelColor('p200');
    const handleAddingDone = React.useCallback((newPickupName) => {
        handleHidePickupPersonField(newPickupName);
    }, [handleHidePickupPersonField]);
    const initials = React.useMemo(() => (pickupName ? getInitials(pickupName) : ''), [pickupName]);
    return (_jsxs(_Fragment, { children: [adding ? (_jsx(AddPickupPersonOverlay, { initialName: pickupName, childName: childName, onDone: handleAddingDone })) : null, _jsx(Stack, { onClick: handleShowPickupPersonField, cursor: "pointer", "data-e2e-id": "custom-pickup-person-container", children: pickupName ? (_jsxs(SelectedStack, { px: { base: 3, tabletPortrait: 6 }, py: { base: 2, tabletPortrait: 3 }, flexDirection: "column", selected: !isExistingRelationSelected, customBorderColor: p300, customBackgroundColor: p200, children: [_jsx(PersonImage, { image: "", initials: initials, size: size }), _jsx(StyledTextContainer, { children: _jsx(Text, { variant: size === 'sm' || size === 'md' ? 'h6' : 'h5', color: "n400", emphasized: true, "data-e2e-id": "custom-pickup-person-text", children: pickupName }) })] })) : (_jsxs(Stack, { px: { base: 3, tabletPortrait: 6 }, py: { base: 2, tabletPortrait: 3 }, flexDirection: "column", "data-e2e-id": "add-custom-pickup-person", children: [_jsx(StyledStack, { children: _jsx(Icon, { name: "add", color: "n300", size: 36 }) }), _jsx(StyledTextContainer, { shouldWrap: true, children: _jsx(Text, { variant: "body-small", color: "n400", emphasized: true, children: translate('addPickupPerson') }) })] })) })] }));
};
const StyledInput = styled.input `
    box-sizing: border-box;
    height: 96px;
    width: 100%;
    padding: 0 16px 0px 68px;
    border: 2px solid ${props => props.theme.mf.colorPalette.n200};
    border-radius: 16px;
    margin-top: 8px;
    font-size: 32px;
    &:focus {
        outline: none;
        border-color: ${props => props.theme.mf.colorPalette.p400};
    }
`;
const ButtonWrapper = styled(createBox({})) `
    button {
        box-sizing: border-box;
        height: 96px;
        min-width: 192px;
        border-radius: 16px;
        margin-top: 8px;
        font-size: 24px;
    }
`;
const AddPickupPersonOverlay = ({ initialName, childName, onDone, }) => {
    const [pickupPersonName, setPickupPersonName] = React.useState(initialName);
    const handleClose = React.useCallback(() => {
        onDone(initialName);
    }, [initialName, onDone]);
    const handleDone = React.useCallback(e => {
        var _a;
        (_a = e === null || e === void 0 ? void 0 : e.stopPropagation) === null || _a === void 0 ? void 0 : _a.call(e);
        onDone(pickupPersonName);
    }, [onDone, pickupPersonName]);
    const handleKeyDown = React.useCallback(e => {
        if (e.keyCode === ENTER_KEY_CODE) {
            onDone(pickupPersonName);
        }
    }, [onDone, pickupPersonName]);
    const handlePickupPersonNameChange = React.useCallback((e) => {
        setPickupPersonName(e.target.value);
    }, [setPickupPersonName]);
    return (_jsx(StyledOverlay, { onClick: handleClose, "data-e2e-id": "custom-pickup-person-overlay", children: _jsxs(Box, { width: "80%", children: [_jsx(Text, { variant: "h6", emphasized: true, children: translate('whoIsPickingUp', { childName }) }), _jsxs(Stack, { spacing: 2, justifyContent: "center", width: "100%", direction: { base: 'column', tabletPortrait: 'row' }, children: [_jsxs(Stack, { position: "relative", flex: 1, children: [_jsx(Stack, { position: "absolute", alignItems: "center", top: "8px", bottom: 0, ml: 4, children: _jsx(Icon, { name: "person_filled", size: 48 }) }), _jsx(StyledInput, { onClick: e => e.stopPropagation(), onKeyDown: handleKeyDown, onChange: handlePickupPersonNameChange, value: pickupPersonName, "data-e2e-id": "add-pickup-person-input" })] }), _jsx(ButtonWrapper, { children: _jsx(Button, { text: "Done", fullWidth: true, onClick: handleDone, "data-e2e-id": "add-custom-pickup-person-done-button" }) })] })] }) }));
};
